import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Shield, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';

const QuienesSomos = () => {
  const valores = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Rigor Técnico",
      description: "Metodologías probadas y estándares profesionales en cada servicio que ofrecemos."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Transparencia Financiera",
      description: "Información clara, oportuna y verificable para la toma de decisiones."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Confidencialidad",
      description: "Manejo responsable y seguro de la información sensible de tu empresa."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Sostenibilidad del Negocio",
      description: "Enfoque en soluciones que generen valor a largo plazo."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Acompañamiento Estratégico",
      description: "Presencia constante como aliados en tu crecimiento empresarial."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        {/* Meta Tags Básicos */}
        <title>Quiénes Somos | Sinergia Empresarial CSCE - Expertos en Finanzas para PYMEs</title>
        <meta name="description" content="Conoce a Sinergia Empresarial CSCE, tu aliado estratégico en dirección financiera y servicios contables. Profesionales expertos en el crecimiento de PYMEs en Colombia." />
        <meta name="keywords" content="empresa consultoría financiera, equipo experto finanzas, PYMEs Colombia, valores empresariales, misión visión consultoría" />
        <link rel="canonical" href="https://www.sinergiaempresarialcsce.com/quienes-somos" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.sinergiaempresarialcsce.com/quienes-somos" />
        <meta property="og:title" content="Quiénes Somos | Sinergia Empresarial CSCE" />
        <meta property="og:description" content="Tu aliado estratégico en dirección financiera y servicios contables. Expertos en el crecimiento de PYMEs en Colombia." />
        <meta property="og:image" content="https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.sinergiaempresarialcsce.com/quienes-somos" />
        <meta name="twitter:title" content="Quiénes Somos | Sinergia Empresarial CSCE" />
        <meta name="twitter:description" content="Tu aliado estratégico en dirección financiera y servicios contables para PYMEs." />
        <meta name="twitter:image" content="https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "mainEntity": {
              "@type": "Organization",
              "name": "Sinergia Empresarial CSCE",
              "url": "https://www.sinergiaempresarialcsce.com",
              "logo": "https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png",
              "description": "Consultoría financiera y servicios contables especializados para PYMEs en Colombia",
              "address": {
                "@type": "PostalAddress",
                "addressCountry": "CO"
              },
              "knowsAbout": [
                "Finanzas Corporativas",
                "Consultoría Estratégica",
                "Contabilidad",
                "Gestión Financiera"
              ]
            }
          })}
        </script>
      </Helmet>

      {/* Header Section */}
      <section className="bg-gradient-to-r from-sinergia-azul to-sinergia-verde text-white py-16">
        <div className="container mx-auto px-6">
          <h1 className="heading-1 text-5xl font-bold mb-4">
            Quiénes Somos
          </h1>
          <p className="body-large max-w-3xl">
            Tu aliado estratégico en dirección financiera y servicios contables para PYMEs.
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-2 text-4xl font-bold mb-6 text-sinergia-gris-oscuro">
                Sinergia Empresarial CSCE
              </h2>
              <p className="body-medium text-gray-700 mb-4">
                Somos una firma especializada en servicios de CFO externo y consultoría financiera, diseñada específicamente para pequeñas y medianas empresas que buscan crecer de forma sostenible y rentable.
              </p>
              <p className="body-medium text-gray-700 mb-4">
                Nuestra misión es democratizar el acceso a dirección financiera de alto nivel, permitiendo que las PYMEs cuenten con la experticia estratégica que antes solo estaba al alcance de grandes corporaciones, pero sin los costos de una nómina fija.
              </p>
              <p className="body-medium text-gray-700">
                Combinamos visión estratégica con ejecución operativa impecable, ofreciendo servicios de CFO externo, consultoría financiera y un centro de servicios compartidos contables que libera a los emprendedores para enfocarse en lo que mejor saben hacer: hacer crecer su negocio.
              </p>
            </div>
            <div>
              <img 
                src="/cubos.jpg"
                alt="Crecimiento empresarial estratégico"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="heading-3 text-2xl font-bold mb-4 text-sinergia-azul">Nuestra Propuesta</h3>
              <p className="body-medium text-gray-700">
                Estrategia financiera y operación contable para que tu empresa crezca con control. 
                Transformamos la forma en que las PYMEs gestionan sus finanzas, combinando metodologías 
                probadas como Profit First con tecnología y un enfoque personalizado.
              </p>
            </div>
            <div className="bg-white rounded-lg p-8 shadow-md">
              <h3 className="heading-3 text-2xl font-bold mb-4 text-sinergia-azul">Nuestro Enfoque</h3>
              <p className="body-medium text-gray-700">
                No creemos en soluciones genéricas. Cada empresa es única, y por eso diseñamos 
                estrategias financieras a la medida, basadas en datos reales y con un enfoque práctico 
                que genera resultados medibles en el corto, mediano y largo plazo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="heading-2 text-4xl font-bold mb-4 text-center text-sinergia-gris-oscuro">
            Nuestros Valores
          </h2>
          <p className="body-medium text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Los principios que guían cada decisión y servicio que ofrecemos.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {valores.map((valor, index) => (
              <div 
                key={index}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="text-sinergia-azul mb-4">
                  {valor.icon}
                </div>
                <h3 className="heading-5 text-xl font-semibold mb-3 text-sinergia-gris-oscuro">
                  {valor.title}
                </h3>
                <p className="body-small text-gray-600">
                  {valor.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section - Sandra Marín y Claudia Gómez */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="heading-2 text-4xl font-bold mb-12 text-center text-sinergia-gris-oscuro">
            Nuestro Equipo
          </h2>

          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Sandra Marín */}
            <div className="bg-white rounded-lg overflow-hidden shadow-xl">
              <div className="grid md:grid-cols-1 items-center">
                <div className="h-96 md:h-full overflow-hidden">
                  <img 
                    src="/Sandra.png"
                    alt="Sandra Marín - CEO y Líder Financiera"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <h3 className="heading-3 text-3xl font-bold mb-2 text-sinergia-gris-oscuro">
                    Sandra Marín
                  </h3>
                  <p className="body-medium text-sinergia-azul font-semibold mb-4">
                    CEO y Líder Financiera
                  </p>
                  <p className="body-medium text-gray-700 mb-4">
                    Especialista en Gerencia Tributaria y Magister en Administración de Empresas y Finanzas Corporativas. 
                    Con amplia experiencia en grandes y medianas empresas, Sandra lidera proyectos de transformación 
                    empresarial ayudando a emprendedores y gerentes a tomar decisiones estratégicas basadas en datos.
                  </p>
                  <p className="body-medium text-gray-700">
                    Su enfoque combina rigor técnico con una profunda comprensión de los desafíos 
                    que enfrentan las empresas en crecimiento, proporcionando soluciones prácticas y 
                    sostenibles que generan resultados reales en gestión financiera y optimización tributaria.
                  </p>
                </div>
              </div>
            </div>

            {/* Claudia Gómez */}
            <div className="bg-white rounded-lg overflow-hidden shadow-xl">
              <div className="grid md:grid-cols-1 items-center">
                <div className="h-96 md:h-full overflow-hidden">
                  <img 
                    src="/Claudia.png"
                    alt="Claudia Gómez - Líder Contable"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <h3 className="heading-3 text-3xl font-bold mb-2 text-sinergia-gris-oscuro">
                    Claudia Gómez
                  </h3>
                  <p className="body-medium text-sinergia-azul font-semibold mb-4">
                    Líder Contable
                  </p>
                  <p className="body-medium text-gray-700 mb-4">
                    Especialista en materia tributaria y experta en costos y presupuestos. 
                    Con sólida experiencia en normativa contable y fiscal colombiana, 
                    Claudia lidera la operación contable del centro de servicios compartidos, 
                    garantizando cumplimiento normativo y optimización de recursos para nuestros clientes.
                  </p>
                  <p className="body-medium text-gray-700">
                    Su conocimiento profundo en NIIF para PYMEs, gestión tributaria y control de costos 
                    permite a las empresas mantener sus operaciones contables al día mientras se enfocan en 
                    su crecimiento, asegurando tranquilidad y conformidad con todas las obligaciones fiscales.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-sinergia-azul text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="heading-2 text-4xl font-bold mb-4">
            ¿Listo para transformar las finanzas de tu empresa?
          </h2>
          <p className="body-large mb-8 max-w-2xl mx-auto">
            Agenda una consulta y descubre cómo podemos ayudarte a alcanzar tus objetivos de negocio.
          </p>
          <Link to="/contacto">
            <Button className="bg-white text-sinergia-azul hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-full">
              Solicita tu consulta
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default QuienesSomos;
